import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Gem, LogOut, ShieldCheck, Bell } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const linkCls = ({ isActive }) =>
  `text-sm tracking-[0.2em] transition-colors duration-300 ${
    isActive ? "text-[#D4AF37]" : "text-gray-400 hover:text-gray-100"
  }`;

export default function Navbar() {
  const { user, logout, openAuth } = useAuth();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const [bellOpen, setBellOpen] = useState(false);

  useEffect(() => {
    if (!user) {
      setNotifs({ items: [], unread: 0 });
      return;
    }
    const load = () => api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [user]);

  const toggleBell = async () => {
    const next = !bellOpen;
    setBellOpen(next);
    if (next && notifs.unread > 0) {
      try {
        await api.post("/notifications/read");
        setNotifs((n) => ({ ...n, unread: 0, items: n.items.map((i) => ({ ...i, read: true })) }));
      } catch {}
    }
  };

  const links = (
    <>
      <NavLink to="/" end className={linkCls} data-testid="nav-home-link">首页</NavLink>
      <NavLink to="/invite" className={linkCls} data-testid="nav-invite-link">邀请</NavLink>
      <NavLink to="/me" className={linkCls} data-testid="nav-me-link">个人中心</NavLink>
    </>
  );

  return (
    <header className="fixed top-0 inset-x-0 z-50 border-b border-[#D4AF37]/15 bg-[#070709]/80 backdrop-blur-xl">
      <div className="mx-auto max-w-7xl px-5 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-brand" className="flex items-center gap-3 group" onClick={() => setBellOpen(false)}>
          <span className="grid place-items-center w-8 h-8 border border-[#D4AF37]/50 rotate-45 transition-transform duration-500 group-hover:rotate-[135deg]">
            <Gem className="w-4 h-4 text-[#D4AF37] -rotate-45 transition-transform duration-500 group-hover:-rotate-[135deg]" />
          </span>
          <span className="font-serif-sc font-bold text-base sm:text-lg tracking-widest">
            合赢<span className="text-gold-grad">项目交流社区</span>
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-8">{links}</nav>
        <div className="flex items-center gap-3">
          {user && (
            <div className="relative">
              <button
                data-testid="notifications-bell"
                onClick={toggleBell}
                className="relative p-2 text-gray-400 hover:text-[#D4AF37] transition-colors"
                aria-label="消息通知"
              >
                <Bell className="w-5 h-5" />
                {notifs.unread > 0 && (
                  <span
                    data-testid="notifications-unread-badge"
                    className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 grid place-items-center text-[10px] bg-[#D4AF37] text-[#070709] font-bold rounded-full"
                  >
                    {notifs.unread}
                  </span>
                )}
              </button>
              {bellOpen && (
                <div
                  data-testid="notifications-panel"
                  className="absolute right-0 top-12 w-80 max-h-96 overflow-y-auto bg-[#0F0F14] border border-[#D4AF37]/25 gold-glow z-50" data-lenis-prevent
                >
                  <p className="text-xs tracking-[0.25em] text-gray-500 px-4 py-3 border-b border-[#D4AF37]/10">消息通知</p>
                  {notifs.items.length === 0 ? (
                    <p className="text-xs text-gray-600 px-4 py-8 text-center" data-testid="notifications-empty">暂无通知</p>
                  ) : (
                    notifs.items.map((n, i) => (
                      <div key={i} className="px-4 py-3 border-b border-[#D4AF37]/5 last:border-0">
                        <div className="flex items-center gap-2 mb-1">
                          {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] shrink-0" />}
                          <p className="text-sm text-gray-100">{n.title}</p>
                          <span className="ml-auto text-[10px] text-gray-600 font-mono-num shrink-0">
                            {(n.created_at || "").slice(5, 16).replace("T", " ")}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 leading-relaxed">{n.content}</p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
          {user ? (
            <>
              <span
                data-testid="nav-user-badge"
                className="hidden sm:inline-flex items-center gap-1.5 text-xs text-[#D4AF37] border border-[#D4AF37]/30 px-3 py-1.5"
              >
                <ShieldCheck className="w-3.5 h-3.5" />
                {user.username} · {user.level_name}
              </span>
              <button
                data-testid="nav-logout-btn"
                onClick={() => {
                  logout();
                  navigate("/");
                }}
                className="text-gray-500 hover:text-white transition-colors p-2"
                aria-label="退出登录"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <div className="flex items-center gap-2.5">
              <button
                data-testid="nav-login-btn"
                onClick={() => openAuth({ mode: "login" })}
                className="btn-ghost-gold text-sm px-4 py-2"
              >
                登录
              </button>
              <button
                data-testid="nav-register-btn"
                onClick={() => openAuth()}
                className="btn-gold text-sm px-5 py-2"
              >
                注册
              </button>
            </div>
          )}
        </div>
      </div>
      <nav className="md:hidden flex justify-center gap-8 pb-3">{links}</nav>
    </header>
  );
}
